import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Redirect } from 'react-router-dom';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import './checkout-form.styles.scss';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  getPaymentIntentRequest,
  stripePaymentSuccessRequest,
} from '../../../../js/api/shopping-cart/_payment-cart';
import {
  cartCountUpdate,
  updateAllowCartStepsAction,
  UpdateCartConfirmParams,
} from '../../../../redux/shopping-cart/shopping-cart.action';
import PaymentInProgressModal from './PaymentInProgressModal';
import { sendPurchaseEventToGA4 } from '../../../../TrackGoogleAnalytics';

// Enhanced Card Options with better security styling
const CARD_OPTIONS_NUMBER = {
  iconStyle: 'solid',
  showIcon: true,
  placeholder: 'Card Number',
  style: {
    base: {
      fontSize: '1rem',
      color: '#424770',
      letterSpacing: '0.025em',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      '::placeholder': {
        color: '#6b7280',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
    complete: {
      color: '#059669',
      iconColor: '#059669',
    },
  },
  // Disable autocomplete for security
  disableLink: true,
};

const CARD_OPTIONS_MMYY = {
  iconStyle: 'default',
  placeholder: 'MM/YY',
  style: {
    base: {
      fontSize: '1rem',
      color: '#424770',
      letterSpacing: '0.025em',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      '::placeholder': {
        color: '#6b7280',
      },
    },
    invalid: {
      color: '#dc2626',
    },
    complete: {
      color: '#059669',
    },
  },
};

const CARD_OPTIONS_CVC = {
  iconStyle: 'default',
  placeholder: 'CVC',
  style: {
    base: {
      fontSize: '1rem',
      color: '#424770',
      letterSpacing: '0.025em',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      '::placeholder': {
        color: '#6b7280',
      },
    },
    invalid: {
      color: '#dc2626',
    },
    complete: {
      color: '#059669',
    },
  },
};

// Enhanced UUID generation with crypto API
const createSecureUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};

// Security validation functions
const validateInput = (value, type) => {
  const patterns = {
    name: /^[a-zA-Z\s'-]{2,50}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^\+?[\d\s-()]{10,}$/,
  };
  
  return patterns[type] ? patterns[type].test(value.trim()) : true;
};

const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.trim().replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
};

// Enhanced CSRF token retrieval
const getCsrfToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]');
  return token ? token.getAttribute('content') : '';
};

function CardField({ onChange, onReady }) {
  const [cardErrors, setCardErrors] = useState({});

  const handleChange = (event) => {
    // Enhanced error handling
    const { elementType, error, complete } = event;
    
    setCardErrors(prev => ({
      ...prev,
      [elementType]: error
    }));

    // Call parent onChange with enhanced data
    onChange({
      ...event,
      allErrors: cardErrors,
      hasAnyError: Object.values({...cardErrors, [elementType]: error}).some(Boolean)
    });
  };

  return (
    <div className="secure-card-container">
      <div className="FormRow">
        <CardNumberElement
          className="react-stripe-elements enhanced"
          options={CARD_OPTIONS_NUMBER}
          onChange={handleChange}
          onReady={onReady}
        />
        {cardErrors.cardNumber && (
          <div className="field-error">{cardErrors.cardNumber.message}</div>
        )}
      </div>
      <br />
      <div>
        <Row>
          <Col className="pr-2">
            <CardExpiryElement
              className="react-stripe-elements enhanced"
              onChange={handleChange}
              options={CARD_OPTIONS_MMYY}
            />
            {cardErrors.cardExpiry && (
              <div className="field-error">{cardErrors.cardExpiry.message}</div>
            )}
          </Col>
          <Col className="pl-2">
            <CardCvcElement
              className="react-stripe-elements enhanced"
              onChange={handleChange}
              options={CARD_OPTIONS_CVC}
            />
            {cardErrors.cardCvc && (
              <div className="field-error">{cardErrors.cardCvc.message}</div>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}

function Field({ 
  label, 
  id, 
  type, 
  required, 
  autoComplete, 
  value, 
  onChange, 
  validationType,
  maxLength = 255
}) {
  const [fieldError, setFieldError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    
    // Validate input
    if (validationType && sanitizedValue) {
      const isValid = validateInput(sanitizedValue, validationType);
      setFieldError(isValid ? '' : `Please enter a valid ${validationType}`);
    } else {
      setFieldError('');
    }

    // Call parent onChange with sanitized value
    onChange({ ...e, target: { ...e.target, value: sanitizedValue } });
  };

  return (
    <div className="FormRow">
      <input
        className={`FormRowInput enhanced ${fieldError ? 'error' : ''} ${isFocused ? 'focused' : ''}`}
        id={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        value={value}
        placeholder={label}
        maxLength={maxLength}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        // Security attributes
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
      />
      {fieldError && <div className="field-error">{fieldError}</div>}
    </div>
  );
}

function SubmitButton({ processing, error, children, disabled, hasValidationErrors }) {
  return (
    <button
      className={`SubmitButton enhanced ${error ? 'SubmitButton--error' : ''} ${processing ? 'processing' : ''}`}
      type="submit"
      disabled={processing || disabled || hasValidationErrors}
      aria-describedby={error ? 'payment-error' : null}
    >
      {processing ? (
        <>
          <span className="spinner" aria-hidden="true"></span>
          Processing Payment...
        </>
      ) : (
        children
      )}
    </button>
  );
}

function ErrorMessage({ children, id = 'payment-error' }) {
  return (
    <div className="ErrorMessage enhanced" role="alert" id={id}>
      <svg width="16" height="16" viewBox="0 0 17 17" aria-hidden="true">
        <path
          fill="#dc2626"
          d="M8.5,17 C3.80557963,17 0,13.1944204 0,8.5 C0,3.80557963 3.80557963,0 8.5,0 C13.1944204,0 17,3.80557963 17,8.5 C17,13.1944204 13.1944204,17 8.5,17 Z"
        />
        <path
          fill="#fff"
          d="M8.5,7.29791847 L6.12604076,4.92395924 C5.79409512,4.59201359 5.25590488,4.59201359 4.92395924,4.92395924 C4.59201359,5.25590488 4.59201359,5.79409512 4.92395924,6.12604076 L7.29791847,8.5 L4.92395924,10.8739592 C4.59201359,11.2059049 4.59201359,11.7440951 4.92395924,12.0760408 C5.25590488,12.4079864 5.79409512,12.4079864 6.12604076,12.0760408 L8.5,9.70208153 L10.8739592,12.0760408 C11.2059049,12.4079864 11.7440951,12.4079864 12.0760408,12.0760408 C12.4079864,11.7440951 12.4079864,11.2059049 12.0760408,10.8739592 L9.70208153,8.5 L12.0760408,6.12604076 C12.4079864,5.79409512 12.4079864,5.25590488 12.0760408,4.92395924 C11.7440951,4.59201359 11.2059049,4.59201359 10.8739592,4.92395924 L8.5,7.29791847 L8.5,7.29791847 Z"
        />
      </svg>
      <span>{children}</span>
    </div>
  );
}

// Security monitoring function
const logSecurityEvent = (eventType, details) => {
  console.warn(`Security Event: ${eventType}`, details);
  // In production, send to your security monitoring system
};

function CheckoutForm() {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [cardReady, setCardReady] = useState(false);
  const [paymentAttempts, setPaymentAttempts] = useState(0);

  // Redux selectors
  const cartitems = useSelector(({ cart }) => cart.cart, shallowEqual);
  const cartitemimpersonate = useSelector(
    ({ cart }) => cart.impersonateMember,
    shallowEqual
  );
  const [orderGuid, setOrderGuid] = useState();
  const normaluser = useSelector(({ user }) => user.loggedin_user);
  const improfileuser = useSelector(({ cart }) =>
    cart.impersonateMember ? cart.impersonateMember.userprofile : null
  );
  const shoppingCartData = useSelector(({ cart }) => cart?.cart);
  const basketId = useSelector(({ cart }) =>
    cart ? cart?.cart?.BasketID : null
  );

  const [userInfo, setUserInfo] = useState(
    improfileuser !== null && improfileuser ? improfileuser : normaluser
  );

  // Security: Verify HTTPS connection
  useEffect(() => {
    if (window.location.protocol !== 'https:' && 
        window.location.hostname !== 'localhost') {
      setError({ message: 'Secure connection required for payments' });
      logSecurityEvent('INSECURE_CONNECTION', { 
        protocol: window.location.protocol,
        hostname: window.location.hostname 
      });
    }
  }, []);

  // Enhanced payment intent retrieval with security
  useEffect(() => {
    let isMounted = true;

    async function checkPaymentIntent() {
      try {
        // Security: Validate required data
        if (!basketId) {
          throw new Error('Invalid basket ID');
        }

        let memberId = null;
        if (cartitemimpersonate) {
          memberId = cartitemimpersonate.memberId;
        }

        const requestData = {
          memberid: memberId,
          basketid: basketId,
          timestamp: Date.now(),
          nonce: createSecureUUID(),
        };

        // Security: Add CSRF token and auth headers
        const paymentIntentResponse = await getPaymentIntentRequest(requestData, {
          headers: {
            'X-CSRF-Token': getCsrfToken(),
            'Content-Type': 'application/json',
          },
        });

        if (!isMounted) return;

        if (paymentIntentResponse) {
          // Security: Validate response
          if (!paymentIntentResponse.IdempotencyKey || !paymentIntentResponse.PaymentIntent) {
            throw new Error('Invalid payment intent response');
          }

          setOrderGuid(paymentIntentResponse.IdempotencyKey);
          const paymentIntent = paymentIntentResponse.PaymentIntent;
          
          if (paymentIntent.ClientSecret) {
            setClientSecret(paymentIntent.ClientSecret);
          } else {
            throw new Error('Missing client secret');
          }
        } else {
          throw new Error('Failed to create payment intent');
        }
      } catch (error) {
        console.error('Payment intent creation failed:', error);
        if (isMounted) {
          setError({ message: 'Failed to initialize payment. Please try again.' });
          logSecurityEvent('PAYMENT_INTENT_FAILURE', { error: error.message });
        }
      }
    }

    checkPaymentIntent();
    return () => { isMounted = false; };
  }, [basketId, cartitemimpersonate]);

  // Enhanced form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name || !validateInput(formData.name, 'name')) {
      errors.name = 'Please enter a valid name (2-50 characters)';
    }
    
    if (!formData.email || !validateInput(formData.email, 'email')) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone || !validateInput(formData.phone, 'phone')) {
      errors.phone = 'Please enter a valid phone number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    const sanitizedValue = sanitizeInput(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Security: Rate limiting
    if (paymentAttempts >= 3) {
      setError({ message: 'Too many payment attempts. Please wait before trying again.' });
      logSecurityEvent('RATE_LIMIT_EXCEEDED', { attempts: paymentAttempts });
      return;
    }

    setPaymentAttempts(prev => prev + 1);

    // Enhanced validation
    if (!validateForm() || !cardComplete) {
      setError({ message: 'Please complete all required fields correctly' });
      return;
    }

    // Security: Verify Stripe is loaded and elements are ready
    if (!stripe || !elements || !cardReady) {
      setError({ message: 'Payment system not ready. Please wait and try again.' });
      return;
    }

    if (error) {
      elements.getElement(CardNumberElement)?.focus();
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Send GA4 event
      sendPurchaseEventToGA4(shoppingCartData);

      const billingDetails = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      // Enhanced payment method creation with retry logic
      let paymentMethodResult;
      let retryCount = 0;
      
      while (retryCount < 2 && !paymentMethodResult) {
        try {
          paymentMethodResult = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardNumberElement),
            billing_details: billingDetails,
          });
          break;
        } catch (err) {
          retryCount++;
          if (retryCount >= 2) throw err;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (paymentMethodResult.error) {
        throw new Error(paymentMethodResult.error.message);
      }

      setPaymentMethod(paymentMethodResult.paymentMethod);

      // Enhanced payment confirmation
      const { id } = paymentMethodResult.paymentMethod;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: id,
        setup_future_usage: 'off_session', // Security: Don't save for future use
      });

      if (result.error) {
        // Handle special case where payment actually succeeded
        if (result.error.payment_intent?.status === 'succeeded') {
          await handleSuccessfulPayment(result.error.payment_intent, 'succeeded');
        } else {
          throw new Error(result.error.message);
        }
      } else {
        // Payment succeeded
        if (result.paymentIntent.status === 'succeeded') {
          await handleSuccessfulPayment(result.paymentIntent, 'succeeded');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setError({ message: error.message || 'Payment processing failed. Please try again.' });
      logSecurityEvent('PAYMENT_PROCESSING_ERROR', { 
        error: error.message,
        userId: userInfo?.Username 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleSuccessfulPayment = async (paymentIntent, status) => {
    try {
      const type = `${paymentIntent.object}.${status}`;
      let memberId = null;
      
      if (cartitemimpersonate) {
        memberId = cartitemimpersonate.memberId;
      }

      const requestData = {
        livemode: paymentIntent.livemode,
        type,
        Data: paymentIntent,
        memberid: memberId,
        isbothcategory: cartitems.IsBothCategory,
        isonlytraining: cartitems.IsOnlyTraining,
        isonlysoftware: cartitems.IsOnlySoftware,
        Username: userInfo.Username,
        // Security: Add timestamp and nonce
        timestamp: Date.now(),
        nonce: createSecureUUID(),
      };

      const stripeResponse = await stripePaymentSuccessRequest(requestData, {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Content-Type': 'application/json',
        },
      });

      if (stripeResponse?.PaymentSuccess) {
        sendPurchaseEventToGA4(
          shoppingCartData,
          true,
          stripeResponse.OrderConfirmationNumber
        );

        setPaymentResult(1);
        
        const categoryparams = {
          isOnlySoftware: cartitems.IsOnlySoftware,
          isOnlyTraining: cartitems.IsOnlyTraining,
          bothCategory: cartitems.IsBothCategory,
          orderConfirmationNumber: stripeResponse.OrderConfirmationNumber,
        };
        
        dispatch(UpdateCartConfirmParams(categoryparams));
        dispatch(cartCountUpdate(1));
        dispatch(updateAllowCartStepsAction(5));
      } else {
        throw new Error('Payment confirmation failed');
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
      setError({ message: 'Payment was processed but confirmation failed. Please contact support.' });
      logSecurityEvent('PAYMENT_CONFIRMATION_ERROR', { 
        error: error.message,
        paymentIntentId: paymentIntent.id 
      });
    }
  };

  // Security: Redirect if payment succeeded
  if (paymentResult) {
    return <Redirect to="/shopping-cart/attendees/review-order/payment/order-confirmation" />;
  }

  // Don't render if client secret isn't available
  if (!clientSecret) {
    return (
      <div className="loading-container">
        <div className="spinner" aria-label="Loading payment form"></div>
        <p>Initializing secure payment...</p>
      </div>
    );
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="shadow-lg p-3 mb-5 bg-white rounded enhanced-security">
      <div className="outer-payment-container">
        <PaymentInProgressModal processing={processing} />
        
        {/* Security notice */}
        <div className="security-notice">
          <span className="security-icon">🔒</span>
          <small>Your payment is secured with 256-bit SSL encryption</small>
        </div>

        <form autoComplete="off" onSubmit={handleSubmit} noValidate>
          <fieldset>
            <div className="combine-row">
              <div className="field">
                <Field
                  label="Name on Card"
                  id="name"
                  type="text"
                  required
                  autoComplete="off"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  validationType="name"
                  maxLength={50}
                />
                {validationErrors.name && (
                  <div className="field-error">{validationErrors.name}</div>
                )}
              </div>
            </div>

            <div className="combine-row">
              <div className="field">
                <Field
                  label="Email"
                  id="email"
                  type="email"
                  required
                  autoComplete="off"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  validationType="email"
                  maxLength={100}
                />
                {validationErrors.email && (
                  <div className="field-error">{validationErrors.email}</div>
                )}
              </div>
              <div className="field">
                <Field
                  label="Phone"
                  id="phone"
                  type="tel"
                  required
                  autoComplete="off"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  validationType="phone"
                  maxLength={20}
                />
                {validationErrors.phone && (
                  <div className="field-error">{validationErrors.phone}</div>
                )}
              </div>
            </div>

            <div className="combine-row card-field">
              <div className="field">
                <CardField
                  onChange={(e) => {
                    setError(e.hasAnyError ? { message: 'Please check your card information' } : null);
                    setCardComplete(e.complete && !e.hasAnyError);
                  }}
                  onReady={() => setCardReady(true)}
                />
              </div>
            </div>

            <br />

            {error && <ErrorMessage>{error.message}</ErrorMessage>}
            
            <SubmitButton
              processing={processing}
              error={error}
              disabled={!stripe || !cardReady}
              hasValidationErrors={hasValidationErrors || !cardComplete}
            >
              <NumberFormat
                value={cartitems ? cartitems.OrderTotal : 0}
                displayType="text"
                thousandSeparator
                prefix="Pay $"
                decimalScale={2}
                fixedDecimalScale
              />
            </SubmitButton>
          </fieldset>
        </form>
      </div>
    </div>
  );
}

export default CheckoutForm;